import type { CSSProperties } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { ScopeButton } from '@monorepo/shared-lib';
import { DRAWER_TABLE_ACTIONS_COLUMN_WIDTH } from './constants';

export const PreviewTableWrapper = styled.div`
  width: 100%;
  /* Allow filter dropdowns to extend outside (with getPopupContainer they render to body; this avoids any local clipping). */
  overflow: visible;

  .validation-errors-table .ant-table-cell {
    padding: 0 6px !important;
  }

  /* Override width: max-content from Custom table so column widths (e.g. Inv/Bill No) are respected */
  .validation-errors-table.transaction-table
    .ant-table-column-sorters
    > .ant-table-column-title,
  .validation-errors-table.transaction-table .ant-table-cell,
  .validation-errors-table.transaction-table .ant-space-item {
    width: auto !important;
    min-width: 0;
  }

  /* Same style as submissions > transaction tab: font size 12px, bold column titles */
  .validation-errors-table.transaction-table .ant-table-thead {
    font-size: 12px;
    font-weight: 600;
    line-height: 15px;
  }

  /* Keep Upload Date / Due Date / Date headers on a single line */
  .validation-errors-table.transaction-table
    th.transactions-date-column
    .ant-table-column-title {
    white-space: nowrap !important;
    width: auto !important;
    min-width: max-content;
  }
  .validation-errors-table.transaction-table .ant-table-thead > tr > th,
  .validation-errors-table.transaction-table .ant-table-thead > tr > td,
  .validation-errors-table.transaction-table .ant-table-thead .ant-table-cell,
  .validation-errors-table.transaction-table
    .ant-table-thead
    .ant-table-column-title {
    font-weight: 700 !important;
    line-height: 15px;
  }
  .validation-errors-table.transaction-table .ant-table-cell {
    font-size: 12px;
    font-weight: 400;
  }

  /* Keep inline-edit inputs at the same 12px font and 26px height as the rest of the table */
  .validation-errors-table.transaction-table .ant-table-cell .ant-input,
  .validation-errors-table.transaction-table
    .ant-table-cell
    .ant-picker-input
    > input,
  .validation-errors-table.transaction-table
    .ant-table-cell
    .ant-select-selection-item,
  .validation-errors-table.transaction-table
    .ant-table-cell
    .ant-select-selection-search-input {
    font-size: 12px;
  }
  .validation-errors-table.transaction-table .ant-table-cell .ant-input,
  .validation-errors-table.transaction-table
    .ant-table-cell
    .ant-select-selector,
  .validation-errors-table.transaction-table .ant-table-cell .ant-picker,
  .validation-errors-table.transaction-table .ant-table-cell .ant-picker-input {
    height: 26px !important;
    min-height: 26px !important;
  }

  /* Prevent row-hover underline from bleeding into inline-edit Select / DatePicker controls */
  .ant-table-row:hover .ant-select-selection-item,
  .ant-table-row:hover .ant-picker-input > input {
    text-decoration: none !important;
  }

  /* Double-click cell hint: prevent underline inheritance, subtle cursor change */
  .ant-table-row:hover .cell-dblclick {
    text-decoration: none !important;
    cursor: text;
  }

  /* Slightly tint rows that are not yet reviewed (NOT_REVIEWED or NONE) */
  .validation-errors-table.transaction-table
    .ant-table-row.transaction-row-not-reviewed
    .ant-table-cell {
    background-color: var(--color-transaction-pending-row, #fefce8) !important;
  }
  .validation-errors-table.transaction-table
    .ant-table-row.transaction-row-not-reviewed:hover
    .ant-table-cell,
  .validation-errors-table.transaction-table
    .ant-table-row.transaction-row-not-reviewed
    > .ant-table-cell-row-hover {
    background-color: var(--color-transaction-pending-row-hover, #fef9c3) !important;
  }

  /* Soft-deleted transactions: keep default row colors (badge shows status) */
  .validation-errors-table.transaction-table
    .ant-table-row.transaction-row-deleted
    .ant-table-cell
    a,
  .validation-errors-table.transaction-table
    .ant-table-row.transaction-row-deleted
    .ant-table-cell
    .anticon {
    text-decoration: none;
  }

  .transaction-status-badge {
    display: inline-flex;
    align-items: center;
    font-size: 10px;
    font-weight: 600;
    line-height: 14px;
    padding: 1px 6px;
    border-radius: 4px;
    text-transform: uppercase;
    white-space: nowrap;
    cursor: default;
    flex-shrink: 0;
  }

  .transaction-status-badge-deleted {
    color: var(--color-status-danger, #d13131);
    background: var(--color-status-danger-bg, #fce7e7);
    border: 1px solid #e8b4b4;
  }

  /* Match paperclip muted color in the attachment / actions column */
  .transaction-history-eye-icon path {
    fill: var(--color-text-muted, #5a6a6a) !important;
  }
`;

export const HistoryToolbar = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

export const HistoryMetaLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 12px;
  line-height: 16px;
  color: var(--color-text-muted, #5a6a6a);

  strong {
    font-weight: 600;
    color: var(--color-text-dark, #042a2a);
  }
`;

export const HistorySearchRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  flex: 1 1 auto;
  flex-wrap: wrap;
`;

export const HistoryViewToggle = styled.div`
  flex-shrink: 0;
`;

export const HistoryTableWrap = styled.div`
  width: 100%;
  overflow: auto;

  .ant-table {
    font-size: 12px;
  }

  .ant-table-thead > tr > th {
    font-weight: 600;
    color: var(--color-text-dark, #042a2a);
    background: var(--color-surface-raised, #ffffff);
  }

  .ant-table-tbody > tr > td {
    color: var(--color-text-dark, #042a2a);
    vertical-align: top;
  }
`;

export const HistoryEmptyState = styled.div`
  padding: 32px 8px;
  text-align: center;
  color: var(--color-text-muted, #5a6a6a);
  font-size: 13px;
  line-height: 18px;
`;

export const HistoryTimeline = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const HistoryTimelineItem = styled.li`
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr);
  gap: 12px;
  align-items: stretch;
`;

export const HistoryTimelineRail = styled.div`
  position: relative;
  display: flex;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    transform: translateX(-50%);
    background: var(--color-border, #dbeae3);
  }

  ${HistoryTimelineItem}:first-child &::before {
    top: 14px;
  }

  ${HistoryTimelineItem}:last-child &::before {
    bottom: calc(100% - 14px);
  }
`;

type HistoryVisualKind =
  | 'original'
  | 'reversal'
  | 'updated'
  | 'deleted'
  | 'current';

const historyKindColors: Record<
  HistoryVisualKind,
  { fg: string; bg: string; border: string; marker: string }
> = {
  original: {
    fg: 'var(--color-status-info, #054581)',
    bg: 'var(--color-status-info-bg, #e1edf6)',
    border: '#b7cfe3',
    marker: 'var(--color-status-info, #054581)',
  },
  updated: {
    fg: 'var(--color-primary, #41aaaf)',
    bg: 'var(--color-layout-bg, #e9f6f7)',
    border: '#b7dde0',
    marker: 'var(--color-primary, #41aaaf)',
  },
  current: {
    fg: 'var(--color-status-success, #058181)',
    bg: 'var(--color-status-success-bg, #e9f6f7)',
    border: '#a9d5d5',
    marker: 'var(--color-status-success, #058181)',
  },
  reversal: {
    fg: 'var(--color-status-warning, #c38a20)',
    bg: 'var(--color-status-warning-bg, #fff5e2)',
    border: '#e6d0a0',
    marker: 'var(--color-status-warning, #c38a20)',
  },
  deleted: {
    fg: 'var(--color-status-danger, #d13131)',
    bg: 'var(--color-status-danger-bg, #fce7e7)',
    border: '#e8b4b4',
    marker: 'var(--color-status-danger, #d13131)',
  },
};

export const HistoryTimelineMarker = styled.span<{ $kind: HistoryVisualKind }>`
  position: relative;
  z-index: 1;
  width: 12px;
  height: 12px;
  margin-top: 18px;
  border-radius: 50%;
  background: ${({ $kind }) => historyKindColors[$kind].marker};
  border: 2px solid #ffffff;
  box-shadow: 0 0 0 1px ${({ $kind }) => historyKindColors[$kind].border};
  flex-shrink: 0;
`;

export const HistoryTimelineCard = styled.article<{ $kind: HistoryVisualKind }>`
  margin-bottom: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  background: var(--color-surface-raised, #ffffff);
  border: 1px solid var(--color-border, #e9f6f7);
  border-inline-start: 3px solid
    ${({ $kind }) => historyKindColors[$kind].marker};
  box-shadow: 0px 0px 10px 0px #0000000d;

  .event-hint,
  .no-changes {
    margin: 8px 0 0;
    font-size: 12px;
    line-height: 16px;
    color: var(--color-text-muted, #5a6a6a);
  }
`;

export const HistoryTimelineHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;

  .header-main {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    min-width: 0;
  }

  .version-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--color-text-dark, #042a2a);
  }

  .event-meta {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: var(--color-text-muted, #5a6a6a);
  }

  .event-actor {
    color: var(--color-text-muted, #5a6a6a);
  }

  .event-date {
    font-size: 12px;
    color: var(--color-text-muted, #5a6a6a);
  }

  .details-toggle {
    appearance: none;
    border: none;
    background: transparent;
    padding: 0;
    margin: 0;
    cursor: pointer;
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    color: var(--color-link, #5e87b0);
    text-decoration: underline;
    text-underline-offset: 2px;

    &:focus-visible {
      outline: 2px solid var(--color-primary, #41aaaf);
      outline-offset: 2px;
      border-radius: 2px;
    }
  }
`;

export const HistoryEventBadge = styled.span<{ $kind: HistoryVisualKind }>`
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 600;
  line-height: 14px;
  padding: 1px 6px;
  border-radius: 4px;
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 0;
  color: ${({ $kind }) => historyKindColors[$kind].fg};
  background: ${({ $kind }) => historyKindColors[$kind].bg};
  border: 1px solid ${({ $kind }) => historyKindColors[$kind].border};
`;

export const HistorySummaryFacts = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px 12px;
  margin-top: 12px;
`;

export const HistorySummaryFact = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  .fact-label {
    font-size: 11px;
    line-height: 14px;
    color: var(--color-text-muted, #5a6a6a);
  }

  .fact-value {
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    color: var(--color-text-dark, #042a2a);
    overflow-wrap: anywhere;
  }
`;

export const HistoryChangeList = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--color-border, #dbeae3);

  .changes-title {
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    color: var(--color-text-dark, #042a2a);
    margin-bottom: 8px;
  }
`;

export const HistoryChangeRow = styled.div`
  display: grid;
  grid-template-columns: minmax(96px, 140px) minmax(0, 1fr);
  gap: 8px 12px;
  align-items: start;
  padding: 6px 0;

  & + & {
    border-top: 1px solid #f3f7f8;
  }

  .change-label {
    font-size: 12px;
    line-height: 16px;
    color: var(--color-text-muted, #5a6a6a);
  }

  .change-values {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    min-width: 0;
    font-size: 12px;
    line-height: 16px;
  }
`;

export const HistoryChangeFrom = styled.span`
  color: var(--color-text-muted, #5a6a6a);
  text-decoration: line-through;
  overflow-wrap: anywhere;
`;

export const HistoryChangeArrow = styled.span`
  color: var(--color-text-muted, #5a6a6a);
`;

export const HistoryChangeTo = styled.span`
  color: var(--color-text-dark, #042a2a);
  font-weight: 600;
  overflow-wrap: anywhere;
`;

// NOTE: `ActionIconButton` was unused, so it has been removed to satisfy linter.

export {
  CustomiseColumnsIconButton as EntityTransactionsToolbarIconButton,
  customiseColumnsIconButtonStyle,
} from 'components/Custom/customiseColumnsToolbar';

/** Shared pill style for Mass update, Activity, Capture Log, and Customise toolbar buttons. */
export const entityTransactionsToolbarButtonStyle = (
  active: boolean
): CSSProperties => ({
  height: '32px',
  width: 'auto',
  backgroundColor: active
    ? 'var(--color-primary-soft, #e6f7f7)'
    : 'var(--color-surface-raised, #FFFFFF)',
  border: `1.5px solid ${
    active
      ? 'var(--color-primary, #41aaaf)'
      : 'var(--color-border, #dbeae3)'
  }`,
  borderRadius: '8px',
  boxShadow: active ? 'none' : '0px 1px 2px 0px #0000000a',
  color: 'var(--color-text-base, #2e2e2e)',
  fontFamily: 'var(--font-family, Inter, sans-serif)',
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '12px',
  padding: '0 12px',
  display: 'flex',
  alignItems: 'center',
});

/** Card body padding aligned with other OrganisationDetails tabs. */
export const EntityTransactionsCardInner = styled.div`
  padding: 12px;
  margin: auto;

  &.entity-transactions-card-fill {
    margin: 0;
    padding: 0;
  }
`;

export const EntityTransactionsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--color-border, #dbeae3);
`;

export const EntityTransactionsHeaderActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

/** Items count + page totals + show-deleted row above the table. */
export const EntityTransactionsMetaBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  gap: 10px 16px;
  margin: 6px 0 8px;
  padding: 0;
`;

/** Totals aligned to the trailing end of the filter row. */
export const EntityTransactionsFilterTotals = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px 12px;
  min-width: 0;
  max-width: 100%;
  text-align: end;
`;

export const EntityTransactionsMetaSummary = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  min-width: 0;
`;

export const EntityTransactionsMetaText = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: var(--color-text-muted, #5a6a6a);

  strong {
    font-weight: 600;
    color: var(--color-text-dark, #042a2a);
  }
`;

export const EntityTransactionsMetaDivider = styled.span`
  display: inline-block;
  width: 1px;
  height: 12px;
  margin: 0 2px;
  background: var(--color-border, #dbeae3);
  vertical-align: middle;
  flex-shrink: 0;
`;

/** Field select + value + Apply — kept as one aligned control group. */
export const EntityTransactionsMassUpdateControls = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;

  /* Force every control in this strip to the same 32px box. */
  > .ant-select,
  > .ant-picker,
  > .ant-input,
  > .ant-btn {
    height: 32px !important;
    box-sizing: border-box !important;
  }

  > .ant-picker {
    width: 160px;
    min-width: 160px;
    padding-inline: 11px !important;
    border-radius: 10px !important;
    border: 1px solid var(--color-border, #dbeae3) !important;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.05) !important;
    display: inline-flex !important;
    align-items: center !important;
  }

  > .ant-picker .ant-picker-input {
    display: flex;
    align-items: center;
    width: 100%;
  }

  > .ant-picker .ant-picker-input > input {
    font-size: 12px !important;
    font-weight: 600 !important;
    line-height: 30px !important;
    height: 30px !important;
  }

  > .ant-input {
    border-radius: 10px !important;
    border: 1px solid var(--color-border, #dbeae3) !important;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.05) !important;
    font-size: 12px !important;
    font-weight: 600 !important;
  }
`;

/** Beat ScopeButton disabled grey-on-orange with higher specificity. */
export const MassUpdateApplyButton = styled(ScopeButton)`
  &&& {
    height: 32px !important;
    min-height: 32px !important;
    padding: 0 14px !important;
    font-size: 12px !important;
    line-height: 16px !important;
    display: inline-flex !important;
    align-items: center !important;
  }

  &&&.ant-btn-disabled,
  &&&:disabled,
  &&&.ant-btn[disabled] {
    background-color: var(--btn-accent-bg, #ffbc3e) !important;
    border-color: var(--btn-accent-bg, #ffbc3e) !important;
    color: var(--btn-dark-text, var(--color-text-dark, #042a2a)) !important;
    opacity: 0.5 !important;
  }

  &&&.ant-btn-disabled span,
  &&&:disabled span,
  &&&.ant-btn[disabled] span {
    color: var(--btn-dark-text, var(--color-text-dark, #042a2a)) !important;
  }
`;

export const EntityTransactionsShowDeleted = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;

  .show-deleted-label {
    font-size: 12px;
    line-height: 16px;
    color: var(--color-text-muted, #5a6a6a);
    white-space: nowrap;
  }
`;

/** Stacked label + control (similar to Edit friendly name / entity details). */
export const EditDrawerFieldStack = styled.li<{
  $compact?: boolean;
  /** Line grid columns: prevent long values from widening the column. */
  $equalColumn?: boolean;
}>`
  padding: ${({ $compact }) => ($compact ? '5px 4px' : '7px 6px')};
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${({ $compact }) => ($compact ? 3 : 5)}px;
  border-radius: 10px;
  list-style: none;
  transition: background-color 0.15s ease;
  min-height: 0;
  ${({ $equalColumn }) =>
    $equalColumn
      ? `
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
  `
      : ''}

  &:hover {
    background-color: var(--color-review-field-hover, #d4edef);
  }
`;

export const EditDrawerStackedLabel = styled.div<{
  $compact?: boolean;
  /** Line grid: keep label row one line so controls align across columns. */
  $equalColumn?: boolean;
}>`
  font-family: Inter, sans-serif;
  font-size: ${({ $compact }) => ($compact ? '10px' : '11px')};
  font-weight: 600;
  line-height: 14px;
  color: var(--color-text-muted, #5a6a6a);
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  min-width: 0;
  ${({ $equalColumn, $compact }) =>
    $equalColumn
      ? `
    flex-wrap: nowrap;
    min-height: ${$compact ? 16 : 18}px;
    overflow: hidden;
  `
      : `
    flex-wrap: wrap;
  `}
`;

export const EditDrawerLabelTrail = styled.div`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 6px;
  white-space: nowrap;
  margin-left: auto;
`;

export const EditDrawerCreateRuleLink = styled.button`
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-primary, #41aaaf);
  font-family: Inter, sans-serif;
  font-size: 11px;
  font-weight: 600;
  line-height: 14px;
  cursor: pointer;
  text-decoration: underline;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    color: var(--color-primary-hover, #64bbbd);
  }

  &:focus-visible {
    outline: 2px solid rgba(65, 170, 175, 0.45);
    outline-offset: 2px;
    border-radius: 2px;
  }

  &.drawer-create-rule-icon-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    border-radius: 4px;
    text-decoration: none;
    flex-shrink: 0;

    &:hover {
      background: rgba(65, 170, 175, 0.12);
    }
  }
`;

export const EditDrawerOptionalInline = styled.span<{ $compact?: boolean }>`
  font-size: ${({ $compact }) => ($compact ? '9px' : '10px')};
  font-weight: 400;
  color: var(--color-text-muted, #9ca8a8);
`;

export const EditDrawerLabelWithTooltip = styled.div<{ $truncate?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  max-width: 100%;
  ${({ $truncate }) =>
    $truncate
      ? `
    flex: 1;
    min-width: 0;
    overflow: hidden;
    & > span:first-child {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
    }
  `
      : ''}
`;

/** Equal-width columns for line fields (1–6); minmax(0,1fr) keeps columns aligned. */
export const EditDrawerLineFieldsGrid = styled.div<{
  $columns: 1 | 2 | 3 | 4 | 5 | 6;
}>`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  column-gap: 24px;
  row-gap: 10px;
  align-items: start;
`;

export const EditDrawerLineFieldsColumn = styled.div`
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
`;

/** Scrollable form / line-items body in the transaction edit drawer. */
export const TransactionEditDrawerLinesScroll = styled.div<{
  $withDeleteGutter?: boolean;
}>`
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding-right: ${({ $withDeleteGutter }) =>
    $withDeleteGutter ? `${8 + DRAWER_TABLE_ACTIONS_COLUMN_WIDTH}px` : '4px'};
  position: relative;
  scrollbar-width: thin;
  scrollbar-color: var(--color-border, #dbeae3) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-border, #dbeae3);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--color-primary, #41aaaf);
  }
`;

/** Matches Document details box chrome in transaction-edit-drawer. */
export const DRAWER_DOCUMENT_SURFACE_BORDER =
  '1px solid var(--color-review-document-surface-border, rgba(219, 234, 227, 0.95))';
export const DRAWER_DOCUMENT_SURFACE_BACKGROUND =
  'var(--color-review-document-surface, rgba(255, 255, 255, 0.72))';
export const DRAWER_DOCUMENT_SURFACE_RADIUS_PX = 8;

/** Toolbar row above the drawer line-items grid (e.g. wrap-text toggle). */
export const TransactionEditDrawerTableToolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-bottom: ${DRAWER_DOCUMENT_SURFACE_BORDER};
  background: ${DRAWER_DOCUMENT_SURFACE_BACKGROUND};
`;

/** Shell: full-width bordered grid, matching Document details card width. */
export const TransactionEditDrawerTableLayout = styled.div`
  position: relative;
  display: block;
  width: 100%;
  min-width: 0;
  overflow: visible;
  box-sizing: border-box;
`;

/**
 * Delete icon lives in the pale drawer gutter, just past the grid border.
 * A 6px gap keeps it from overlapping Total.
 */
export const TransactionEditDrawerDeleteRail = styled.div`
  position: absolute;
  top: 0;
  left: calc(100% + 6px);
  width: ${DRAWER_TABLE_ACTIONS_COLUMN_WIDTH}px;
  bottom: 0;
  z-index: 3;
  pointer-events: none;
`;

export const TransactionEditDrawerDeleteRailSlot = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 2px;
  pointer-events: auto;
  border-radius: 4px;

  &[data-active='true'],
  &:hover {
    background: #e8f3f6;
  }

  .drawer-table-delete-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    margin: 0;
    padding: 0;
    border: none;
    border-radius: 4px;
    background: transparent;
    box-shadow: none;
    color: var(--color-text-muted, #5a6a6a);
    font-size: 13px;
    line-height: 1;
    cursor: pointer;
  }

  @media (hover: hover) and (pointer: fine) {
    .drawer-table-delete-btn {
      opacity: 0;
      pointer-events: none;
    }

    &[data-active='true'] .drawer-table-delete-btn,
    &:hover .drawer-table-delete-btn,
    .drawer-table-delete-btn:focus-visible {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .drawer-table-delete-btn:hover:not(:disabled) {
    color: var(--color-text-dark, #042a2a);
  }

  .drawer-table-delete-btn:focus-visible {
    outline: 2px solid var(--color-primary, #41aaaf);
    outline-offset: 1px;
    opacity: 1;
    pointer-events: auto;
  }

  .drawer-table-delete-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

/** Editable line-items table inside the transaction review drawer. */
export const TransactionEditDrawerTableWrap = styled.div<{
  $textWrap?: boolean;
  $columnWidthsLocked?: boolean;
}>`
  width: 100%;
  min-width: 0;
  border-radius: ${DRAWER_DOCUMENT_SURFACE_RADIUS_PX}px;
  border: ${DRAWER_DOCUMENT_SURFACE_BORDER};
  background: ${DRAWER_DOCUMENT_SURFACE_BACKGROUND};
  overflow: hidden;

  ${({ $columnWidthsLocked }) =>
    $columnWidthsLocked
      ? `
    .drawer-table-col-resize-handle {
      display: none !important;
      pointer-events: none !important;
    }
  `
      : ''}

  .transaction-edit-drawer-lines-table {
    table-layout: fixed;
    width: 100%;
  }

  /* Vertical: table grows with rows (parent drawer scrolls).
     Horizontal: only .ant-table-content scrolls so header/body stay aligned. */
  .transaction-edit-drawer-lines-table.ant-table-wrapper,
  .transaction-edit-drawer-lines-table .ant-table-container,
  .transaction-edit-drawer-lines-table .ant-table-header,
  .transaction-edit-drawer-lines-table .ant-table-body {
    max-height: none !important;
    overflow-y: visible !important;
  }

  .transaction-edit-drawer-lines-table .ant-table-content {
    overflow-x: auto !important;
    overflow-y: hidden !important;
    scrollbar-width: thin;
    scrollbar-color: #dbeae3 transparent;
  }

  .transaction-edit-drawer-lines-table .ant-table-content::-webkit-scrollbar {
    height: 8px;
  }

  .transaction-edit-drawer-lines-table
    .ant-table-content::-webkit-scrollbar-track {
    background: transparent;
  }

  .transaction-edit-drawer-lines-table
    .ant-table-content::-webkit-scrollbar-thumb {
    background: #dbeae3;
    border-radius: 4px;
  }

  .transaction-edit-drawer-lines-table
    .ant-table-content::-webkit-scrollbar-thumb:hover {
    background: var(--color-primary, #41aaaf);
  }

  /* Match Document details box surface */
  .transaction-edit-drawer-lines-table .ant-table,
  .transaction-edit-drawer-lines-table .ant-table-container,
  .transaction-edit-drawer-lines-table .ant-table-content,
  .transaction-edit-drawer-lines-table table {
    border: none !important;
    background: transparent;
  }

  .transaction-edit-drawer-lines-table.ant-table-wrapper,
  .transaction-edit-drawer-lines-table .ant-table-container {
    padding: 0 !important;
    margin: 0 !important;
  }

  /* Continuous grid — borders on cells (not inputs) so lines align across header/body/rows */
  .transaction-edit-drawer-lines-table table,
  .transaction-edit-drawer-lines-table .ant-table-header > table,
  .transaction-edit-drawer-lines-table .ant-table-body > table,
  .transaction-edit-drawer-lines-table .ant-table-content > table {
    border-collapse: collapse !important;
    border-spacing: 0 !important;
    table-layout: fixed !important;
    /* Fill the bordered card — no empty delete gutter inside the grid */
    width: 100% !important;
    min-width: 100% !important;
  }

  .transaction-edit-drawer-lines-table .ant-table-thead > tr > th,
  .transaction-edit-drawer-lines-table .ant-table-tbody > tr > td,
  .transaction-edit-drawer-lines-table .ant-table-summary > tr > td,
  .transaction-edit-drawer-lines-table .ant-table-cell {
    border-color: var(--color-border, #dbeae3) !important;
    border-style: solid !important;
    border-width: 0 1px 1px 0 !important;
  }

  .transaction-edit-drawer-lines-table
    .ant-table-thead
    > tr
    > th.drawer-table-line-index-col,
  .transaction-edit-drawer-lines-table
    .ant-table-tbody
    > tr
    > td.drawer-table-line-index-col {
    border-left-width: 1px !important;
  }

  .transaction-edit-drawer-lines-table .ant-table-thead > tr > th {
    border-top-width: 1px !important;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.25;
    white-space: normal;
    word-break: break-word;
    hyphens: auto;
    padding: 10px 2px !important;
    vertical-align: top;
    text-align: left;
    background: ${DRAWER_DOCUMENT_SURFACE_BACKGROUND} !important;
    color: var(--color-text-base, #2e2e2e) !important;
  }

  .transaction-edit-drawer-lines-table .ant-table-tbody > tr > td {
    padding: 0 !important;
    vertical-align: middle;
    background: transparent;
  }

  .transaction-edit-drawer-lines-table
    .ant-table-tbody
    > tr
    > td.ant-table-cell {
    padding: 0 !important;
    vertical-align: middle;
  }

  .transaction-edit-drawer-lines-table .ant-table-thead .ant-table-cell {
    vertical-align: top;
    padding: 10px 2px !important;
    background: ${DRAWER_DOCUMENT_SURFACE_BACKGROUND} !important;
  }

  .transaction-edit-drawer-lines-table .drawer-table-col-header {
    display: block;
    max-width: 100%;
  }

  /* Column resize: right-edge handle on field headers (not the line-index col). */
  .transaction-edit-drawer-lines-table .react-resizable {
    position: relative;
    background-clip: padding-box;
  }

  .transaction-edit-drawer-lines-table .drawer-table-col-resize-handle {
    position: absolute;
    top: 0;
    right: -5px;
    z-index: 2;
    width: 10px;
    height: 100%;
    cursor: col-resize;
    touch-action: none;
  }

  .transaction-edit-drawer-lines-table
    .drawer-table-col-resize-handle::after {
    content: '';
    position: absolute;
    top: 20%;
    bottom: 20%;
    left: 4px;
    width: 2px;
    border-radius: 1px;
    background: transparent;
    transition: background 0.12s ease;
  }

  .transaction-edit-drawer-lines-table
    th:hover
    > .drawer-table-col-resize-handle::after,
  .transaction-edit-drawer-lines-table
    .drawer-table-col-resize-handle:hover::after,
  .transaction-edit-drawer-lines-table
    .drawer-table-col-resize-handle:active::after {
    background: var(--color-primary, #41aaaf);
  }

  /* Hide default SE corner handle from react-resizable package CSS */
  .transaction-edit-drawer-lines-table
    .react-resizable-handle:not(.drawer-table-col-resize-handle) {
    display: none !important;
  }

  .transaction-edit-drawer-lines-table .drawer-table-col-optional {
    display: block;
    margin-top: 2px;
    font-size: 9px;
    font-weight: 500;
    color: var(--color-text-muted, #9ca8a8);
    line-height: 1.2;
  }

  .transaction-edit-drawer-lines-table .drawer-table-col-header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    min-width: 0;
  }

  .transaction-edit-drawer-lines-table .drawer-table-col-header-label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Line index column — subtle strip on the document surface */
  .transaction-edit-drawer-lines-table th.drawer-table-line-index-col,
  .transaction-edit-drawer-lines-table td.drawer-table-line-index-col {
    background: ${DRAWER_DOCUMENT_SURFACE_BACKGROUND} !important;
  }

  .transaction-edit-drawer-lines-table .drawer-table-line-label {
    display: block;
    font-weight: 600;
    font-size: 10px;
    color: var(--color-primary, #41aaaf);
    text-align: center;
    line-height: 28px;
  }

  .transaction-edit-drawer-lines-table .ant-table-tbody > tr > td > div {
    width: 100%;
    margin: 0;
    padding: 0;
    display: block;
  }

  .transaction-edit-drawer-lines-table .ant-table-tbody > tr > td > div > div {
    width: 100%;
    max-width: 100%;
  }

  /* Controls fill cells; grid lines come from td/th borders above */
  .transaction-edit-drawer-lines-table .ant-input,
  .transaction-edit-drawer-lines-table .ant-picker,
  .transaction-edit-drawer-lines-table .ant-select,
  .transaction-edit-drawer-lines-table .scope-form-select.ant-select {
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    border: none !important;
    box-shadow: none !important;
    box-sizing: border-box !important;
    min-height: 28px !important;
    height: 28px !important;
    background: transparent !important;
  }

  .transaction-edit-drawer-lines-table .ant-input,
  .transaction-edit-drawer-lines-table .ant-picker,
  .transaction-edit-drawer-lines-table .ant-select .ant-select-selector,
  .transaction-edit-drawer-lines-table
    .scope-form-select.ant-select
    .ant-select-selector {
    min-height: 28px !important;
    height: 28px !important;
    font-size: 11px !important;
    font-weight: 600 !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    box-sizing: border-box !important;
    background: transparent !important;
  }

  .transaction-edit-drawer-lines-table .scope-form-select.ant-select:hover,
  .transaction-edit-drawer-lines-table
    .scope-form-select.ant-select.ant-select-focused,
  .transaction-edit-drawer-lines-table
    .scope-form-select.ant-select.ant-select-open {
    border: none !important;
    box-shadow: none !important;
  }

  .transaction-edit-drawer-lines-table
    .scope-form-select.ant-select:hover
    .ant-select-selector,
  .transaction-edit-drawer-lines-table
    .scope-form-select.ant-select.ant-select-focused
    .ant-select-selector,
  .transaction-edit-drawer-lines-table
    .scope-form-select.ant-select.ant-select-open
    .ant-select-selector {
    border: none !important;
    box-shadow: none !important;
  }

  .transaction-edit-drawer-lines-table .ant-picker .ant-picker-input > input {
    font-size: 11px !important;
  }

  .transaction-edit-drawer-lines-table .ant-picker-suffix,
  .transaction-edit-drawer-lines-table .ant-picker-suffix .anticon,
  .transaction-edit-drawer-lines-table .ant-picker-clear,
  .transaction-edit-drawer-lines-table .ant-select-arrow {
    color: var(--color-primary, #41aaaf) !important;
  }

  .transaction-edit-drawer-lines-table .ant-table-cell .ant-select {
    width: 100%;
    min-height: 28px !important;
    height: 28px !important;
  }

  .transaction-edit-drawer-lines-table .drawer-table-amount-col .ant-input {
    text-align: right;
    padding-left: 2px !important;
    padding-right: 3px !important;
  }

  .transaction-edit-drawer-lines-table .ant-table-summary > tr > td {
    padding: 6px 3px !important;
    vertical-align: middle;
    background: ${DRAWER_DOCUMENT_SURFACE_BACKGROUND} !important;
    overflow: hidden;
  }

  .transaction-edit-drawer-lines-table
    .ant-table-summary
    > tr
    > td.drawer-table-line-index-col {
    border-left-width: 1px !important;
  }

  .transaction-edit-drawer-lines-table .drawer-table-summary-amount {
    display: block;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.25;
    color: var(--color-text-dark, #042a2a);
    text-align: right;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .transaction-edit-drawer-lines-table .drawer-table-summary-label {
    display: block;
    padding: 0 6px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.25;
    color: var(--color-text-dark, #042a2a);
    white-space: nowrap;
  }

  .transaction-edit-drawer-lines-table .drawer-table-discount-col .ant-input,
  .transaction-edit-drawer-lines-table .drawer-table-tax-col .ant-input {
    padding-left: 1px !important;
    padding-right: 3px !important;
  }

  .transaction-edit-drawer-lines-table .drawer-table-description-col {
    width: 100%;
    min-width: 0;
  }

  .transaction-edit-drawer-lines-table
    .drawer-table-description-col
    .ant-input {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .transaction-edit-drawer-lines-table
    .drawer-table-description-col
    .ant-select,
  .transaction-edit-drawer-lines-table
    .drawer-table-description-col
    .ant-select
    .ant-select-selector {
    width: 100% !important;
    max-width: 100% !important;
  }

  ${({ $textWrap }) =>
    $textWrap
      ? `
  .transaction-edit-drawer-lines-table .ant-table-tbody > tr > td,
  .transaction-edit-drawer-lines-table
    .ant-table-tbody
    > tr
    > td.ant-table-cell {
    vertical-align: top;
  }

  .transaction-edit-drawer-lines-table .drawer-table-line-label {
    line-height: 1.25;
    padding: 6px 0;
  }

  .transaction-edit-drawer-lines-table .drawer-table-text-col .ant-input,
  .transaction-edit-drawer-lines-table .drawer-table-text-col textarea.ant-input {
    height: auto !important;
    min-height: 28px !important;
    overflow: visible !important;
    text-overflow: clip !important;
    white-space: pre-wrap !important;
    word-break: break-word !important;
    line-height: 1.35 !important;
    padding: 4px 6px !important;
    resize: none;
  }

  .transaction-edit-drawer-lines-table .drawer-table-text-col .ant-select,
  .transaction-edit-drawer-lines-table
    .drawer-table-text-col
    .scope-form-select.ant-select {
    height: auto !important;
    min-height: 28px !important;
  }

  .transaction-edit-drawer-lines-table
    .drawer-table-text-col
    .ant-select
    .ant-select-selector,
  .transaction-edit-drawer-lines-table
    .drawer-table-text-col
    .scope-form-select.ant-select
    .ant-select-selector {
    height: auto !important;
    min-height: 28px !important;
    align-items: flex-start !important;
    padding-top: 4px !important;
    padding-bottom: 4px !important;
    white-space: normal !important;
  }

  .transaction-edit-drawer-lines-table
    .drawer-table-text-col
    .ant-select-selection-item,
  .transaction-edit-drawer-lines-table
    .drawer-table-text-col
    .ant-select-selection-placeholder {
    overflow: visible !important;
    text-overflow: clip !important;
    white-space: normal !important;
    word-break: break-word !important;
    line-height: 1.35 !important;
    max-width: 100% !important;
  }

  .transaction-edit-drawer-lines-table
    .drawer-table-text-col
    .ant-select-selection-overflow,
  .transaction-edit-drawer-lines-table
    .drawer-table-text-col
    .ant-select-selection-search {
    overflow: visible !important;
  }

  .transaction-edit-drawer-lines-table .drawer-table-summary-label {
    white-space: normal;
    word-break: break-word;
  }
  `
      : ''}
`;

export const EditDrawerControlTooltipTarget = styled.div`
  display: block;
  min-width: 0;
  width: 100%;
  max-width: 100%;
`;

export const EditDrawerQuestionMarkWrapper = styled.span`
  color: var(--color-text-muted, #b5b5b5) !important;
  display: inline-flex;
  cursor: pointer;
  margin-top: -2px;

  & svg,
  & path {
    fill: currentColor !important;
  }
`;

/** Field controls: 45px extra from label given to input area. */
const EDIT_DRAWER_FIELD_BORDER = '1px solid var(--color-border, #dbeae3)';
/** Matches project validation/error styling (UploadTransactionsPanel, MessageBox, inputs). */
const EDIT_DRAWER_FIELD_ERROR_BORDER =
  '1px solid var(--color-status-danger, #d13131)';
const EDIT_DRAWER_FIELD_ERROR_BG =
  'var(--color-status-danger-bg, #fce7e7)';

/** 2-column grid: cap above a typical one-third column (~280–300px) on wide drawer so fields edge out past 3-column width. */
export const EDIT_DRAWER_TWO_COLUMN_CONTROL_MAX_WIDTH_PX = 400;

/** Field controls: same as map import MapFormSelect (12px, 33px height, border var(--color-border, #dbeae3), line-height). */
export const EditDrawerControlWrap = styled.div<{
  $fullWidth?: boolean;
  /** When set (e.g. 2-column layout), max width so inputs stay slightly wider than typical 3-column cells. */
  $capControlWidthPx?: number;
  /** Line field grid: fill column width; truncate overflow (see tooltips). */
  $equalColumn?: boolean;
  /** Required-field validation failed for this control. */
  $invalid?: boolean;
}>`
  ${({ $fullWidth, $capControlWidthPx, $equalColumn }) =>
    $equalColumn
      ? `
    flex: 1 1 0;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    .ant-input {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `
      : $fullWidth
      ? `
    flex: 0 1 auto;
    width: 100%;
    min-width: 0;
    max-width: ${
      $capControlWidthPx != null ? `${$capControlWidthPx}px` : 'none'
    };
  `
      : `
    flex: 0 0 200px;
    max-width: 200px;
  `}
  .ant-input,
  .ant-picker {
    font-size: 12px !important;
    font-weight: 600 !important;
    min-height: 33px !important;
    height: 33px !important;
    border: ${EDIT_DRAWER_FIELD_BORDER} !important;
    border-radius: 0;
    box-sizing: border-box !important;
  }
  .ant-picker .ant-picker-input > input {
    font-weight: 600 !important;
  }
  .ant-picker {
    min-height: 33px !important;
    height: 33px !important;
  }
  .ant-picker .ant-picker-input {
    height: 100%;
  }
  .ant-picker .ant-picker-input > input {
    font-size: 12px !important;
  }
  .ant-picker-suffix,
  .ant-picker-suffix .anticon,
  .ant-picker-clear,
  .ant-picker-clear .anticon {
    color: var(--color-primary, #41aaaf) !important;
  }
  .ant-picker-suffix svg,
  .ant-picker-clear svg {
    fill: currentColor !important;
  }
  /* Select: border on selector (same 33px box as inputs); outer wrapper must not clip bottom edge */
  .ant-select {
    min-height: 33px !important;
    height: 33px !important;
    width: 100%;
    border: none !important;
    box-shadow: none !important;
    box-sizing: border-box !important;
  }
  .ant-select .ant-select-selector,
  /* FormSelect adds scope-form-select; index.scss Ant overrides set transparent borders — restore drawer chrome */
  .scope-form-select.ant-select .ant-select-selector {
    font-size: 12px !important;
    font-weight: 600 !important;
    min-height: 33px !important;
    height: 33px !important;
    border: ${EDIT_DRAWER_FIELD_BORDER} !important;
    border-radius: 0;
    box-sizing: border-box !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    display: flex !important;
    align-items: center !important;
  }
  .scope-form-select.ant-select {
    border: 1px solid transparent !important;
    box-shadow: none !important;
  }
  .ant-select:hover,
  .ant-select.ant-select-focused,
  .ant-select.ant-select-open,
  .scope-form-select.ant-select:hover,
  .scope-form-select.ant-select.ant-select-focused,
  .scope-form-select.ant-select.ant-select-open {
    /* Keep 1px so hover does not drop the transparent wrapper border (layout jump). */
    border: 1px solid transparent !important;
    box-shadow: none !important;
  }
  .ant-select:hover .ant-select-selector,
  .ant-select.ant-select-focused .ant-select-selector,
  .ant-select.ant-select-open .ant-select-selector,
  .scope-form-select.ant-select:hover .ant-select-selector,
  .scope-form-select.ant-select.ant-select-focused .ant-select-selector,
  .scope-form-select.ant-select.ant-select-open .ant-select-selector {
    border-color: var(--color-border, #dbeae3) !important;
    box-shadow: none !important;
  }
  .ant-select-selection-item,
  .ant-select-selection-placeholder,
  .ant-select-selection-search-input {
    line-height: 20px !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }
  .ant-select-selection-overflow,
  .ant-select-selection-search {
    max-width: 100% !important;
    overflow: hidden !important;
  }
  .ant-select .ant-select-arrow {
    color: var(--color-primary, #41aaaf) !important;
  }
  .ant-select .ant-select-arrow .anticon {
    color: inherit !important;
  }
  .ant-select .ant-select-selection-item {
    font-weight: 600 !important;
  }
  ${({ $invalid }) =>
    $invalid
      ? `
    .ant-input,
    .ant-picker {
      border: ${EDIT_DRAWER_FIELD_ERROR_BORDER} !important;
      background-color: ${EDIT_DRAWER_FIELD_ERROR_BG} !important;
    }
    .ant-select .ant-select-selector {
      border: ${EDIT_DRAWER_FIELD_ERROR_BORDER} !important;
      background-color: ${EDIT_DRAWER_FIELD_ERROR_BG} !important;
    }
    .scope-form-select.ant-select .ant-select-selector {
      border: ${EDIT_DRAWER_FIELD_ERROR_BORDER} !important;
      background-color: ${EDIT_DRAWER_FIELD_ERROR_BG} !important;
    }
    .ant-select:hover .ant-select-selector,
    .ant-select.ant-select-focused .ant-select-selector,
    .ant-select.ant-select-open .ant-select-selector,
    .scope-form-select.ant-select:hover .ant-select-selector,
    .scope-form-select.ant-select.ant-select-focused .ant-select-selector,
    .scope-form-select.ant-select.ant-select-open .ant-select-selector {
      border-color: var(--color-status-danger, #d13131) !important;
    }
  `
      : ''}
`;

/** Dropdown menu items: same 12px font as the trigger. */
export const EditDrawerSelectDropdownStyle = createGlobalStyle`
  .edit-drawer-select-dropdown.ant-select-dropdown {
    /* DrawerContainer uses z-index 1200 (above sidebar 1100); keep menus above the drawer. */
    z-index: 1300 !important;
  }

  .edit-drawer-select-dropdown.ant-select-dropdown .ant-select-item {
    font-size: 12px !important;
  }

  /* PWA review sheet: wrap long labels; never let virtual-list inner exceed the panel */
  .scope-pwa-review-select-dropdown.ant-select-dropdown .ant-select-item-option-content {
    white-space: normal !important;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
`;

/** Mass update toolbar: single clean border on the selector (no outer+inner double ring). */
export const MassUpdateFieldSelectHighlightStyle = createGlobalStyle`
  /* Kill FilterSelect / scope-filter-select outer chrome — it stacks with the selector. */
  .mass-update-field-select.ant-select,
  .mass-update-field-select.scope-filter-select.ant-select {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
    height: 32px !important;
    min-height: 32px !important;
    padding: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
  }

  .mass-update-field-select.ant-select .ant-select-selector,
  .mass-update-field-select.ant-select:not(.ant-select-disabled):hover
    .ant-select-selector,
  .mass-update-field-select.ant-select.ant-select-focused
    .ant-select-selector,
  .mass-update-field-select.ant-select.ant-select-open .ant-select-selector,
  .mass-update-field-select.scope-filter-select.ant-select
    .ant-select-selector,
  .mass-update-field-select.scope-filter-select.ant-select:not(
      .ant-select-disabled
    ):hover
    .ant-select-selector,
  .mass-update-field-select.scope-filter-select.ant-select.ant-select-focused
    .ant-select-selector,
  .mass-update-field-select.scope-filter-select.ant-select.ant-select-open
    .ant-select-selector {
    border: 1.5px solid var(--color-primary, #41aaaf) !important;
    border-radius: 10px !important;
    background-color: var(--color-primary-soft, #e6f7f7) !important;
    box-shadow: none !important;
    height: 32px !important;
    min-height: 32px !important;
    padding-inline: 11px !important;
    display: flex !important;
    align-items: center !important;
  }

  .mass-update-field-select.ant-select .ant-select-selection-item,
  .mass-update-field-select.ant-select .ant-select-selection-placeholder {
    color: var(--color-text-dark, #042a2a) !important;
    font-weight: 600 !important;
    font-size: 12px !important;
    line-height: 30px !important;
    inset-inline-start: 11px !important;
  }

  .mass-update-field-select.ant-select .ant-select-arrow {
    color: var(--color-text-muted, #5a6a6a) !important;
  }

  /* Hide native input placeholder so it cannot stack on Ant's placeholder. */
  .mass-update-field-select.ant-select
    .ant-select-selection-search-input::placeholder {
    color: transparent !important;
    opacity: 0 !important;
  }
`;

/**
 * Segmented layout toggles — scoped descendants so styling survives Ant Design attaching
 * `className` to a wrapper (our old `.txn-review-drawer-segmented.ant-segmented` single-node rule broke).
 */
export const TransactionReviewDrawerSegmentedWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  min-width: 0;

  & .ant-segmented {
    padding: 2px !important;
    background: var(--color-review-segmented-track, rgba(255, 255, 255, 0.96)) !important;
    border: 1px solid var(--color-review-document-surface-border, rgba(219, 234, 227, 0.95)) !important;
    border-radius: 10px !important;
    box-shadow: 0 1px 2px rgba(4, 42, 42, 0.05);
  }

  & .ant-segmented .ant-segmented-item-label {
    font-size: 11px !important;
    font-weight: 600 !important;
    line-height: 26px !important;
    padding: 0 11px !important;
    min-height: 26px !important;
    color: var(--color-segmented-item, #5a6a6a) !important;
  }

  &
    .ant-segmented
    .ant-segmented-item-selected:not(.ant-segmented-item-disabled) {
    background-color: var(--color-primary, #41aaaf) !important;
    border-radius: 8px !important;
    box-shadow: 0 1px 3px rgba(65, 170, 175, 0.32) !important;
  }

  &
    .ant-segmented
    .ant-segmented-item-selected:not(.ant-segmented-item-disabled)
    .ant-segmented-item-label {
    color: var(--color-review-segmented-selected-fg, #ffffff) !important;
    font-weight: 600 !important;
  }

  & .ant-segmented .ant-segmented-thumb {
    background-color: transparent !important;
    box-shadow: none !important;
  }

  &
    .ant-segmented
    .ant-segmented-item:not(.ant-segmented-item-selected):not(
      .ant-segmented-item-disabled
    ):hover {
    color: var(--color-text-dark, #042a2a) !important;
  }

  &
    .ant-segmented
    .ant-segmented-item:not(.ant-segmented-item-selected):not(
      .ant-segmented-item-disabled
    ):hover
    .ant-segmented-item-label {
    color: var(--color-text-dark, #042a2a) !important;
  }

  & .ant-segmented .ant-segmented-item:focus-visible {
    outline: 2px solid rgba(65, 170, 175, 0.45);
    outline-offset: 1px;
  }
`;

/** File card in the right-hand uploads panel; hover reveals Rescan and Delete actions. */
export const TransactionsFileCardWrap = styled.div`
  width: 100%;
  margin-bottom: 8px;
  overflow: hidden;
  position: relative;

  .transactions-file-panel--pwa & {
    margin-bottom: 0;
  }

  .transactions-file-card-body {
    transition: padding-right 0.12s ease;
  }

  .transactions-file-card-hover-actions {
    position: absolute;
    top: 6px;
    right: 7px;
    z-index: 2;
    display: none;
    align-items: center;
    gap: 6px;
  }

  &:hover .transactions-file-card-hover-actions,
  &.transactions-file-card-wrap--filter-active
    .transactions-file-card-hover-actions,
  &:hover .transactions-file-card-reviewed-hover-delete,
  &.transactions-file-card-wrap--filter-active
    .transactions-file-card-reviewed-hover-delete {
    display: inline-flex;
  }

  /*
   * Filenames use full card width by default. On hover, reserve only enough
   * room for the visible action(s) — Delete alone is ~70px, Rescan+Delete ~150px.
   * PWA uses a menu only; gutters are handled in index.scss under
   * .transactions-file-panel--pwa (do not apply these desktop paddings there).
   */
  &:hover:not(.transactions-file-card-wrap--posted)
    .transactions-file-card-body,
  &.transactions-file-card-wrap--filter-active:not(
      .transactions-file-card-wrap--posted
    )
    .transactions-file-card-body {
    padding-right: 72px !important;
  }

  &:hover.transactions-file-card-wrap--has-rescan:not(
      .transactions-file-card-wrap--posted
    )
    .transactions-file-card-body,
  &.transactions-file-card-wrap--filter-active.transactions-file-card-wrap--has-rescan:not(
      .transactions-file-card-wrap--posted
    )
    .transactions-file-card-body {
    padding-right: 148px !important;
  }

  /* Always-on selection checkbox only needs a little room until Delete appears. */
  &.transactions-file-card-wrap--upload-select .transactions-file-card-body {
    padding-right: 44px !important;
  }

  &.transactions-file-card-wrap--upload-select:hover
    .transactions-file-card-body,
  &.transactions-file-card-wrap--upload-select.transactions-file-card-wrap--filter-active
    .transactions-file-card-body {
    padding-right: 132px !important;
  }

  /* PWA file list: only room for the ⋯ control — never the desktop action gutter. */
  .transactions-file-panel--pwa &:hover:not(.transactions-file-card-wrap--posted)
    .transactions-file-card-body,
  .transactions-file-panel--pwa
    &.transactions-file-card-wrap--filter-active:not(
      .transactions-file-card-wrap--posted
    )
    .transactions-file-card-body,
  .transactions-file-panel--pwa
    &:hover.transactions-file-card-wrap--has-rescan:not(
      .transactions-file-card-wrap--posted
    )
    .transactions-file-card-body,
  .transactions-file-panel--pwa
    &.transactions-file-card-wrap--filter-active.transactions-file-card-wrap--has-rescan:not(
      .transactions-file-card-wrap--posted
    )
    .transactions-file-card-body,
  .transactions-file-panel--pwa
    &.transactions-file-card-wrap--upload-select
    .transactions-file-card-body,
  .transactions-file-panel--pwa
    &.transactions-file-card-wrap--upload-select:hover
    .transactions-file-card-body,
  .transactions-file-panel--pwa
    &.transactions-file-card-wrap--upload-select.transactions-file-card-wrap--filter-active
    .transactions-file-card-body {
    padding-right: 40px !important;
  }

  .transactions-file-card-reviewed-hover-delete {
    display: none;
    align-items: center;
    white-space: nowrap;
  }

  .transactions-file-card-reviewed-actions {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .transactions-file-card-hover-action {
    box-sizing: border-box;
    height: 28px;
    padding: 0 12px;
    font-size: 11px;
    font-weight: 400;
    line-height: 1;
    border-radius: 7px;
    border: 2px solid var(--color-border, #dbeae3);
    background-color: var(--color-surface-raised, #ffffff);
    color: var(--color-text-dark, #042a2a);
    box-shadow: 0px 0px 10px 0px #0000000d;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: Inter, sans-serif;
    white-space: nowrap;

    &:hover {
      border-color: var(--color-primary, #41aaaf);
    }
  }
`;

export const ProviderPostSplitButton = styled.div`
  display: inline-flex;
  flex-shrink: 0;
  align-items: stretch;

  > .ant-btn:first-child {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }

  .provider-post-split-arrow {
    display: inline-flex;
    height: 30px;
  }

  .provider-post-split-arrow .ant-btn {
    width: 30px !important;
    min-width: 30px !important;
    padding: 0 !important;
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    border-left-width: 0 !important;
  }
`;

export const ProviderPostDropdownMenuStyle = createGlobalStyle`
  .provider-post-dropdown-menu {
    max-width: calc(100vw - 32px - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px));
  }

  .app-root--pwa-mobile .provider-post-dropdown-menu {
    margin-inline: 16px;
  }

  .provider-post-dropdown-menu .ant-dropdown-menu {
    min-width: 200px;
    border-radius: 10px;
    border: 1px solid #e9f6f7;
    box-shadow: 10px 10px 10px 0px #0000000d;
    padding: 12px !important;
  }

  .provider-post-dropdown-menu .ant-dropdown-menu-item {
    font-size: 14px !important;
    line-height: 20px !important;
    font-weight: 400 !important;
    padding: 4px 2px !important;
    border-radius: 4px;
    color: var(--color-text-dark, #042a2a) !important;
  }

  .provider-post-dropdown-menu .ant-dropdown-menu-item:hover {
    background-color: #fff5e2 !important;
    color: var(--color-text-dark, #042a2a) !important;
  }
`;

export { StyledUnsavedChangesModal } from 'components/Custom/ConfirmModal';
